import { UpcomingWork, UpcomingWorkApprovalStatus } from '@jms/model';
import { defineStatusTag } from '../../shared/define.status.tag';

/**
 * The approval pill on a task-grain row — for the CARD and KANBAN renderers only.
 *
 * The board TABLE cannot reach this: `presentation.tags` is read by those two renderers, while
 * `wb-value` picks its renderer from field metadata alone. The table's pill comes from the badge
 * options on `approvalStatus` in `upcoming.work.metadata.ts` (API side), which carry the same labels
 * and the same tones. Change one and change the other, or the same fact renders two ways.
 *
 * TONES ARE CHOSEN BY WHAT THE OPERATOR SHOULD DO, not by how good the news is:
 *
 * - `Approved` is success — nothing to do.
 * - `Changed` is warning — the work moved under an approval that no longer covers it, and someone has
 *   to re-approve. It is the only actionable state here, so it is the only one that carries urgency.
 * - `NeedsReview` is neutral — the validity check has not settled. Colouring it would send an operator
 *   chasing a row that resolves itself.
 * - `NotApproved` is neutral — most forecast work has never been near an approval, and painting the
 *   ordinary case red would make the column noise the operator learns to ignore.
 *
 * `NotApproved` is deliberately NOT `error`: nothing is wrong with unapproved forecast work. The review
 * board's `Unavailable` is error because a row that cannot be approved at all IS a fault; absence of an
 * approval is not.
 */
export const upcomingWorkApprovalTag = defineStatusTag<UpcomingWorkApprovalStatus, UpcomingWork>(
  {
    [UpcomingWorkApprovalStatus.Approved]: 'success',
    [UpcomingWorkApprovalStatus.Changed]: 'warning',
    [UpcomingWorkApprovalStatus.NeedsReview]: 'neutral',
    [UpcomingWorkApprovalStatus.NotApproved]: 'neutral',
  },
  // The enum members are wire values; two of them are not English. Spelled out here rather than renamed
  // in the model, because the wire value is shared with the API and the CSV and must not drift.
  { labels: { [UpcomingWorkApprovalStatus.NeedsReview]: 'Needs review', [UpcomingWorkApprovalStatus.NotApproved]: 'Not approved' } },
);
